import api from './api';
import { 
  Meeting, 
  CreateMeetingRequest, 
  UpdateMeetingRequest, 
  MeetingTokenResponse,
  MeetingPermissionsResponse,
  MeetingsPaginatedResponse 
} from '../types';
import { API_ENDPOINTS } from '../constants';

const meetingService = {
  /**
   * Get paginated list of meetings
   */
  getMeetings: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    meeting_type?: string;
    status?: string;
    cluster_id?: string;
    store_id?: string;
    created_by?: string;
  }): Promise<MeetingsPaginatedResponse> => {
    // Filter out empty/undefined parameters to avoid validation errors
    const filteredParams = params ? Object.fromEntries(
      Object.entries(params).filter(([_, value]) => 
        value !== undefined && value !== null && value !== ''
      )
    ) : {};
    
    const response = await api.get<{ success: boolean; message: string; data: MeetingsPaginatedResponse }>(
      API_ENDPOINTS.MEETINGS.LIST, 
      { params: filteredParams }
    );
    return response.data.data;
  },

  /**
   * Get meeting by ID
   */
  getMeetingById: async (id: string): Promise<Meeting> => {
    const response = await api.get<{ success: boolean; message: string; data: Meeting }>(
      API_ENDPOINTS.MEETINGS.GET_BY_ID(id)
    );
    return response.data.data;
  },

  /**
   * Create new meeting
   */
  createMeeting: async (meetingData: CreateMeetingRequest): Promise<Meeting> => {
    const response = await api.post<{ success: boolean; message: string; data: Meeting }>(
      API_ENDPOINTS.MEETINGS.CREATE,
      meetingData
    );
    return response.data.data;
  },

  /**
   * Update meeting
   */
  updateMeeting: async (id: string, meetingData: UpdateMeetingRequest): Promise<Meeting> => {
    const response = await api.put<{ success: boolean; message: string; data: Meeting }>(
      API_ENDPOINTS.MEETINGS.UPDATE(id),
      meetingData
    );
    return response.data.data;
  },

  /**
   * Delete meeting
   */
  deleteMeeting: async (id: string): Promise<void> => {
    await api.delete(API_ENDPOINTS.MEETINGS.DELETE(id));
  },

  /**
   * Join meeting
   */
  joinMeeting: async (id: string): Promise<MeetingTokenResponse> => {
    const response = await api.post<{ success: boolean; message: string; data: MeetingTokenResponse }>(
      API_ENDPOINTS.MEETINGS.JOIN(id)
    );
    return response.data.data;
  },

  /**
   * Leave meeting
   */
  leaveMeeting: async (id: string): Promise<void> => {
    await api.post(API_ENDPOINTS.MEETINGS.LEAVE(id));
  },

  /**
   * Start meeting
   */
  startMeeting: async (id: string): Promise<Meeting> => {
    const response = await api.post<{ success: boolean; message: string; data: Meeting }>(
      API_ENDPOINTS.MEETINGS.START(id)
    );
    return response.data.data;
  },

  /**
   * End meeting
   */
  endMeeting: async (id: string): Promise<Meeting> => {
    const response = await api.post<{ success: boolean; message: string; data: Meeting }>(
      API_ENDPOINTS.MEETINGS.END(id)
    );
    return response.data.data;
  },

  /**
   * Get meeting token for joining
   */
  getMeetingToken: async (id: string): Promise<MeetingTokenResponse> => {
    const response = await api.get<{ success: boolean; message: string; data: MeetingTokenResponse }>(
      API_ENDPOINTS.MEETINGS.TOKEN(id)
    );
    return response.data.data;
  },

  /**
   * Get meeting permissions
   */
  getMeetingPermissions: async (): Promise<MeetingPermissionsResponse> => {
    const response = await api.get<{ success: boolean; message: string; data: MeetingPermissionsResponse }>(
      API_ENDPOINTS.MEETINGS.PERMISSIONS
    );
    return response.data.data;
  },

  /**
   * Update meeting permissions
   */
  updateMeetingPermissions: async (permissions: MeetingPermissionsResponse): Promise<MeetingPermissionsResponse> => {
    const response = await api.put<{ success: boolean; message: string; data: MeetingPermissionsResponse }>(
      API_ENDPOINTS.MEETINGS.UPDATE_PERMISSIONS,
      permissions
    );
    return response.data.data;
  },
};

export default meetingService;
